import React, { useCallback, useRef } from 'react';
import { View, Text, TouchableOpacity, Keyboard, StyleSheet } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';
import { format } from 'date-fns';
import LinkifiedText from '@/components/LinkifiedText';

export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

export interface MessageBubbleAnchor {
  x: number;
  y: number;
  width: number;
  height: number;
  isUser: boolean;
  text: string;
}

interface MessageBubbleProps {
  message: ChatMessage;
  previousMessage: ChatMessage | null;
  nextMessage: ChatMessage | null;
  isBookmarked: boolean;
  contextMenuVisible: boolean;
  onLongPress: (messageId: string, anchor: MessageBubbleAnchor) => void;
}

const MessageBubble = React.memo(({
  message,
  nextMessage,
  isBookmarked,
  contextMenuVisible,
  onLongPress,
}: MessageBubbleProps) => {
  const isConsecutiveToNextSender = nextMessage && nextMessage.isUser === message.isUser;
  const marginBottom = isConsecutiveToNextSender ? 4 : 16;

  const wrapperRef = useRef<View>(null);

  const handleLongPressItem = useCallback(() => {
    const node = wrapperRef.current;
    if (node && typeof node.measureInWindow === 'function') {
      node.measureInWindow((x, y, width, height) => {
        onLongPress(message.id, {
          x,
          y,
          width,
          height,
          isUser: message.isUser,
          text: message.text,
        });
      });
    } else {
      onLongPress(message.id, {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        isUser: message.isUser,
        text: message.text,
      });
    }
  }, [onLongPress, message.id, message.isUser, message.text]);

  return (
    <View
      ref={wrapperRef}
      collapsable={false}
      style={[styles.messageWrapper, { marginBottom }]}
    >
      <TouchableOpacity
        activeOpacity={0.8}
        onPress={() => Keyboard.dismiss()}
        onLongPress={handleLongPressItem}
        delayLongPress={500}
        style={styles.messageContainerWrapper}
      >
        <Animated.View
          entering={FadeIn.duration(500)}
          style={[
            styles.messageContainer,
            message.isUser ? styles.userMessage : styles.agentMessage,
            isBookmarked && styles.bookmarkedMessage,
            contextMenuVisible && styles.selectedMessage,
          ]}
        >
          <LinkifiedText
            style={[
              styles.messageText,
              message.isUser ? styles.userMessageText : styles.agentMessageText,
            ]}
            linkStyle={[
              styles.linkText,
              message.isUser ? styles.userLinkText : styles.agentLinkText,
            ]}
          >
            {message.text}
          </LinkifiedText>
          <View style={styles.messageFooter}>
            <Text
              style={[
                styles.timestampText,
                message.isUser ? styles.userTimestampText : styles.agentTimestampText,
              ]}
            >
              {format(message.timestamp, 'HH:mm')}
            </Text>
            {isBookmarked && (
              <Ionicons
                name="bookmark"
                size={12}
                color={message.isUser ? 'rgba(255, 255, 255, 0.6)' : '#A19F97'}
                style={styles.bookmarkIcon}
              />
            )}
          </View>
        </Animated.View>
      </TouchableOpacity>
    </View>
  );
});

export default MessageBubble;

const styles = StyleSheet.create({
  messageWrapper: {
    position: 'relative',
  },
  messageContainerWrapper: {
    width: '100%',
  },
  messageContainer: {
    maxWidth: '85%',
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#151515',
    borderRadius: 16,
    borderBottomRightRadius: 4,
    padding: 16,
  },
  agentMessage: {
    alignSelf: 'flex-start',
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderBottomLeftRadius: 4,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    padding: 16,
  },
  messageText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    lineHeight: 22,
  },
  userMessageText: {
    color: '#FFFFFF',
  },
  agentMessageText: {
    color: '#151515',
  },
  messageFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-end',
    marginTop: 4,
    gap: 6,
  },
  timestampText: {
    fontSize: 10,
    fontFamily: 'Inter_400Regular',
  },
  userTimestampText: {
    color: 'rgba(255, 255, 255, 0.6)',
  },
  agentTimestampText: {
    color: '#A19F97',
  },
  bookmarkIcon: {
    marginTop: 1,
  },
  bookmarkedMessage: {
    borderWidth: 1,
    borderColor: '#DAD8CF',
  },
  selectedMessage: {
    zIndex: 1000,
    elevation: 1000,
    shadowColor: '#000',
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
  },
  linkText: {
    textDecorationLine: 'underline',
    fontWeight: '500',
  },
  userLinkText: {
    color: '#87CEEB',
  },
  agentLinkText: {
    color: '#015480',
  },
});
